// Read-only lineup row for the Scoreboard tab's per-manager carousel.
// Visually matches TeamSlot.js (logo, state line, points, captain/5x badges)
// but drops every interactive affordance: no drag, no swipe-to-cut, no
// tap-to-toggle. This is not the owner's editing surface, it's everyone
// else looking in.
import React, { useState } from "react";
import { Crown, Zap, Snowflake, ChevronDown } from "lucide-react";
import { SUPER_DOG_MARK, SUPER_DOG_LEGEND } from "../../utils/superDog";
import TeamLogoImage from "../league/TeamLogoImage";
import { calculateCombinedPoints, getOutcomeCategory, getTeamSpread, shouldShowSpread } from "../../utils/gameStatus";
import { getWeeklyPoints } from "../../utils/teamStats";
import { explainTeamFantasyPoints, describeMultiplier } from "../../utils/scoreBreakdown";
import FreezeDetail from "../lineup/FreezeDetail";

// One accent color per outcome. A full row tint would fight with the text for
// attention on a page whose whole point is being scannable at a glance, so
// this is a stripe, not a background.
//
// A win that did not cover is the lineup blue, not a paler green: on a 4px
// stripe the two greens read as the same color, so the one distinction the
// stripe exists to make was lost. Exported so the page legend uses the same
// values rather than a second copy.
export const OUTCOME_COLORS = {
  "win-cover": "var(--color-success)",
  "win-nocover": "var(--color-brand)",
  "loss-cover": "var(--color-outcome-losscover)",
  "loss-nocover": "var(--color-danger)",
  push: "var(--color-text-faint)",
  upcoming: "var(--color-border)",
  bye: "var(--color-border)",
};

function ReadOnlyTeamRow({ slug, index, team, gameInfo, gameDisplayInfo, gameSpread, isSuperDog, isCaptain, isTripPlay, frozenPoints, freezeDetail, week, weeklyPoints, onOpenTeam }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  // See TeamSlot: an interrupted game reports whether it has actually kicked
  // off, since the type alone cannot say.
  const gameStarted = gameDisplayInfo
    && gameDisplayInfo.type !== "upcoming"
    && gameDisplayInfo.type !== "bye-week"
    && gameDisplayInfo.hasStarted !== false;
  // Prefer the map the page refetches while games are live, and fall back to
  // the boot time AuthContext copy when a caller does not supply one.
  const baseWeeklyPts = weeklyPoints
    ? getWeeklyPoints({ weeklyPoints }, week)
    : getWeeklyPoints(team?.currentSeason, week);
  const weeklyPts = gameStarted ? calculateCombinedPoints(baseWeeklyPts, isCaptain, isTripPlay) : null;
  // Set only for a frozen starter: the score it was frozen at, from the
  // lineup's team_points, multiplier included. See TeamSlot.
  const isFrozen = typeof frozenPoints === "number";
  const displayPoints = isFrozen ? frozenPoints
    : gameStarted && weeklyPts != null ? weeklyPts.finalPoints
    : null;
  // Same "how was this made" panel as TeamSlot on the Lineup tab, same
  // reasons: not for a frozen score (captured mid-game, the game keeps
  // playing underneath it, so today's game state could describe a different
  // number than the one actually frozen).
  const canExplainScore = gameStarted && !isFrozen && weeklyPts != null && gameInfo;
  const scoreBreakdown = canExplainScore
    ? explainTeamFantasyPoints({
        won: gameInfo.homeScore > gameInfo.awayScore,
        tied: gameInfo.homeScore === gameInfo.awayScore,
        spread: getTeamSpread(gameInfo, slug),
        actualMargin: gameInfo.homeScore - gameInfo.awayScore,
        isLive: Boolean(gameDisplayInfo?.isLive),
      })
    : null;
  const multiplier = describeMultiplier({ isCaptain, isTripPlay });
  // The outcome stripe on the left already carries win/loss/cover, so the 5x
  // Play gets a background tint instead of a second accent competing for the
  // same edge. Same tint TeamSlot.js uses for the identical state on the
  // Lineup tab.
  // A frozen row outranks the 5x tint. The 5x is visible in the multiplier on
  // the score and in the zap beside the name either way, while a freeze only
  // ever showed as one small snowflake against a score that otherwise looks
  // ordinary, and it is the state most likely to be read as a mistake ("why
  // is this 10 when the game finished 3?"). Same icy tint as TeamSlot uses
  // for the identical state on the Lineup tab.
  // Same stripe as the lineup's rows, and the same reason for the alternate
  // being --color-bg-row-alt rather than the page ground: on white the two
  // were a hair apart, but in dark the page is pure black against a #1C1C1E
  // card, which is a big enough step that the stripe reads as a state.
  const rowBg = isFrozen ? "var(--color-freeze-bg)" : isTripPlay ? "var(--color-tripplay-bg)" : index % 2 === 0 ? "var(--color-bg-card)" : "var(--color-bg-row-alt)";
  const outcome = getOutcomeCategory(gameInfo, slug);
  const stripeColor = OUTCOME_COLORS[outcome] || OUTCOME_COLORS.upcoming;

  return (
    <div className="flex" style={{ backgroundColor: rowBg, position: "relative" }}>
      <div style={{ width: 4, flexShrink: 0, backgroundColor: stripeColor }} />
      {/* The frost ring goes on the content, not the outer row: the outcome
          stripe owns the left 4px and a ring around the whole thing would
          paint over it. */}
      <div
        className="flex-1 px-3 py-2"
        style={isFrozen ? {
          boxShadow: "inset 0 0 0 1px var(--color-freeze-border)",
          position: "relative",
        } : undefined}
      >
      <div className="grid grid-cols-[36px,1fr,auto] gap-2 items-center">
        <div className="relative">
          <TeamLogoImage teamName={team?.school || slug} primaryColor={team?.colors?.primary} size={28} />
          {gameDisplayInfo?.isLive && (
            <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: "var(--color-success)" }} />
          )}
        </div>

        {/* Same target as TeamSlot on the Lineup tab: the name block, not the
            logo, because it is the bigger one and the one people reach for.
            The deck cancels the click that ends a swipe, so a gesture that
            finishes over a row does not also open the sheet. */}
        <div
          className="min-w-0"
          onClick={() => onOpenTeam?.(team?.school || slug)}
          style={{ cursor: onOpenTeam ? "pointer" : "default" }}
        >
          <div className="font-semibold text-[14px] leading-tight flex items-center gap-1" style={{ color: "var(--color-text-strong)" }}>
            <span className="truncate">{team?.school || slug}</span>
            {isCaptain && <Crown size={11} className="flex-shrink-0" style={{ color: "var(--color-captain)" }} strokeWidth={2.5} />}
            {isTripPlay && <Zap size={11} className="flex-shrink-0" style={{ color: "var(--color-tripplay)" }} strokeWidth={2.5} />}
            {/* A labelled chip, not a bare snowflake: the icon alone asked a
                manager to already know what it meant, on the one row whose
                score no longer matches the game beside it. */}
            {isFrozen && (
              <span
                className="flex-shrink-0 flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold"
                style={{ backgroundColor: "var(--color-freeze-bg)", color: "var(--color-freeze-text)" }}
                title={`Frozen at ${displayPoints}`}
              >
                <Snowflake size={9} strokeWidth={2.5} />
                FROZEN
              </span>
            )}
            {/* The spread sits on the name line as a chip, not at the tail of
                the grey line below. It was the first thing truncated there,
                and it is the number that says whether a win was ever likely:
                negative means favoured, matching scoring.js. Tinted by which
                side of the line the team is on, so favourite and underdog are
                distinguishable without reading the sign. */}
            {gameSpread && shouldShowSpread(gameDisplayInfo) && (
              <span
                className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold"
                style={
                  gameSpread === "PICK"
                    ? { backgroundColor: "var(--color-bg-subtle)", color: "var(--color-text-muted)" }
                    : gameSpread.startsWith("-")
                      ? { backgroundColor: "var(--color-band-bg)", color: "var(--color-brand)" }
                      : { backgroundColor: "var(--color-captain-fill)", color: "var(--color-captain-text)" }
                }
                title={
                  gameSpread === "PICK"
                    ? "No favourite"
                    : gameSpread.startsWith("-")
                      ? `Favoured by ${gameSpread.slice(1)}`
                      : isSuperDog
                        ? `Underdog by ${gameSpread.slice(1)}. ${SUPER_DOG_LEGEND}`
                        : `Underdog by ${gameSpread.slice(1)}`
                }
              >
                {gameSpread}
                {/* Same mark as the home page and the lineup. The chip is
                    already tinted for an underdog, and that tint says nothing
                    about whether this one is big enough to carry the rule. */}
                {isSuperDog && <span aria-label="Super Dog"> {SUPER_DOG_MARK}</span>}
              </span>
            )}
          </div>
          {/* Opponent first. getGameDisplayInfo already resolves it (subtext
              carries the venue prefix and the opponent's real school name),
              and it was being computed and thrown away here: the row said
              when a game kicked off without ever saying who it was against.
              It leads the line because it is the thing you cannot work out
              from anywhere else on the card, and the tail truncates first. */}
          <div className="text-xs truncate" style={{ color: "var(--color-text-muted)" }}>
            {/* A bye sets both halves to the same fact ("No game this week"
                and "BYE WEEK"), so it reads as one line rather than two ways
                of saying it. */}
            {gameDisplayInfo?.type === "bye-week"
              ? "No game this week"
              : [gameDisplayInfo?.subtext, gameDisplayInfo?.display || "No game scheduled"]
                .filter(Boolean)
                .join(" · ")}
          </div>
        </div>

        {scoreBreakdown || isFrozen ? (
          <button
            type="button"
            onClick={() => setShowBreakdown((v) => !v)}
            aria-expanded={showBreakdown}
            aria-label={`How ${team?.school || slug}'s score of ${displayPoints} was made`}
            className="flex items-center gap-0.5 justify-end font-bold text-sm"
            style={{
              color: isFrozen ? "var(--color-freeze)" : isCaptain ? "var(--color-captain-deep)" : isTripPlay ? "var(--color-tripplay)" : "var(--color-brand)",
              background: "none", border: "none", padding: 0, cursor: "pointer",
            }}
          >
            {displayPoints}
            <ChevronDown size={12} style={{ transform: showBreakdown ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
          </button>
        ) : (
          <div className="text-right font-bold text-sm" style={{ color: isFrozen ? "var(--color-freeze)" : isCaptain ? "var(--color-captain-deep)" : isTripPlay ? "var(--color-tripplay)" : "var(--color-brand)" }}>
            {displayPoints ?? "—"}
          </div>
        )}
      </div>

      {/* Same panel as TeamSlot.js on the Lineup tab: a Win/Loss line, the
          spread cover or miss, Super Dog and the underdog bonus when they
          apply, then the captain/5x multiplier, in the order
          calculateTeamFantasyPoints applies them in backend/scoring.js. */}
      {showBreakdown && (
        <div className="mt-2 pt-2 text-xs" style={{ borderTop: "1px solid var(--color-bg-subtle)" }}>
          {isFrozen ? (
            <FreezeDetail
              detail={freezeDetail}
              frozenPoints={displayPoints}
              currentPoints={gameStarted && weeklyPts != null ? weeklyPts.finalPoints : null}
              gameOver={gameDisplayInfo?.type === "completed"}
              spread={getTeamSpread(gameInfo, slug)}
              isCaptain={isCaptain}
              isTripPlay={isTripPlay}
            />
          ) : scoreBreakdown && (
            <>
              {scoreBreakdown.lines.map((line, i) => (
                <div key={i} className="flex justify-between py-0.5">
                  <span style={{ color: "var(--color-text-muted)" }}>{line.label}</span>
                  <span style={{ fontWeight: 600, color: line.points < 0 ? "var(--color-danger)" : line.points > 0 ? "var(--color-success)" : "var(--color-text-muted)" }}>
                    {line.points > 0 ? `+${line.points}` : line.points}
                  </span>
                </div>
              ))}
              <div className="flex justify-between py-0.5" style={{ borderTop: "1px solid var(--color-bg-subtle)", marginTop: 2, paddingTop: 4 }}>
                <span style={{ color: "var(--color-text-muted)" }}>Raw score</span>
                <span style={{ fontWeight: 700, color: "var(--color-text-strong)" }}>{scoreBreakdown.total > 0 ? `+${scoreBreakdown.total}` : scoreBreakdown.total}</span>
              </div>
              {multiplier && (
                <div className="flex justify-between py-0.5">
                  <span style={{ color: "var(--color-text-muted)" }}>{multiplier.label}</span>
                  <span style={{ fontWeight: 600, color: "var(--color-text-strong)" }}>&times;{multiplier.factor}</span>
                </div>
              )}
              {gameDisplayInfo?.isLive && (
                <p className="mt-1" style={{ color: "var(--color-text-muted)", margin: "4px 0 0" }}>
                  Live. Updates as the game continues.
                </p>
              )}
            </>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

export default ReadOnlyTeamRow;
